import { useMemo } from 'react';
import { InitiativeStatus } from '../../../../api/initiative-client/initiative-client.type';
import { useProcessAnalysisStore } from '../../../../store/process-analysis-store-v2/process-analysis-store-v2';
import { getStatusColor, getStatusLabel } from './initiative-overview.helpers';
import type { StatusOption } from './initiative-overview.type';

const STATUS_ORDER: InitiativeStatus[] = [
  InitiativeStatus.OnTrack,
  InitiativeStatus.AtRisk,
  InitiativeStatus.OffTrack,
  InitiativeStatus.Unknown,
  InitiativeStatus.Empty,
];

/**
 * Custom hook that extracts unique statuses from fetched initiatives
 * and creates status options with labels and colors, sorted by enum order
 *
 * @returns {StatusOption[]} Array of available status options sorted by enum order
 */
const useAvailableInitiativeStatuses = (): StatusOption[] => {
  const initiatives = useProcessAnalysisStore((state) => state.initiatives);

  return useMemo(() => {
    const statusSet = new Set<InitiativeStatus>();

    initiatives.forEach((initiative) => {
      const status = initiative.status?.by_tasks?.status || initiative.status?.by_points?.status;
      if (status) {
        statusSet.add(status);
      }
    });

    return STATUS_ORDER.filter((status) => statusSet.has(status)).map(
      (status): StatusOption => ({
        status,
        label: getStatusLabel(status),
        color: getStatusColor(status),
      }),
    );
  }, [initiatives]);
};

export { useAvailableInitiativeStatuses };
